import { Fragment, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { AxiosError } from 'axios';

import { AppRoutes } from '@/constants';
import { useCurrentProfile, useLogout } from '@/hooks';
import { navigate } from '@/providers';
import { useBreadcrumbStore } from '@/stores';
import { getBreadcrumbItems, getRoleVariant } from '@/utils';

import { ChevronDownIcon, ChevronUpIcon, Code02Icon, LogoIcon, LogOut01Icon } from '@/assets/icons';
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Loading,
  Separator,
  SvgIcon,
} from '@/components/common';
import { Alert } from '@/lib/ui';

export const Header = () => {
  const location = useLocation();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const { mutateAsync: logout, isPending: isLoggingOut } = useLogout();
  const { data: profile, error } = useCurrentProfile();
  const appVersion = (process.env.REACT_APP_VERSION ?? '').replace(/^app_version:\s*/, '').trim();
  const sortedRoles = [...(profile?.roles ?? [])].sort((a, b) => a.localeCompare(b));

  const { labelOverrides, extraItems } = useBreadcrumbStore();

  const rawItems = getBreadcrumbItems(location.pathname).map((item) => ({
    ...item,
    label: labelOverrides?.[item.url] ?? item.label,
  }));

  const breadcrumbItems = [...rawItems, ...(extraItems ?? [])].map((item, i, arr) => ({
    ...item,
    isLast: i === arr.length - 1,
  }));

  if (
    location.pathname !== AppRoutes.INTERNAL_SERVER_ERROR &&
    (error as AxiosError)?.code === 'ERR_NETWORK'
  ) {
    // when BE is not available, redirect to internal server error page
    navigate(AppRoutes.INTERNAL_SERVER_ERROR);
    return null;
  }

  const onLogout = async () => {
    try {
      await logout();
      Alert.success('Logged out successfully');
      navigate(AppRoutes.LOGIN, { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <header className="sticky w-full bg-neutral-0 shadow-sm border-neutral-3 border-b-[2px] border-solid z-[20] h-[56px] flex flex-row items-center justify-between px-6">
      <div className="flex items-center justify-between w-full gap-3">
        <div className="flex flex-row items-center gap-5">
          <LogoIcon
            onClick={() => {
              navigate(AppRoutes.HOME);
            }}
            className="hover:cursor-pointer"
          />
          <Separator className="h-6 w-[1px] bg-neutral-3" />
          {breadcrumbItems.length > 0 && (
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbItems.map((item, index) => (
                  <Fragment key={index}>
                    <BreadcrumbItem>
                      {item.isLast || item.isDisabled ? (
                        <BreadcrumbPage>{item.label}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink asChild>
                          <Link to={item.url}>{item.label}</Link>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                    {index < breadcrumbItems.length - 1 && <BreadcrumbSeparator />}
                  </Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          )}
        </div>

        <DropdownMenu onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <div className="flex flex-row items-center gap-2 cursor-pointer">
              <span className="text-body-sm font-medium text-neutral-9 whitespace-nowrap">
                {profile?.fullName}
              </span>
              {sortedRoles.map((role) => (
                <Badge key={role} border variant={getRoleVariant(role)} size="sm" mode="light">
                  {role}
                </Badge>
              ))}
              <SvgIcon
                className="w-4 h-4 text-neutral-9"
                component={isDropdownOpen ? ChevronUpIcon : ChevronDownIcon}
              />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            sideOffset={20}
            align="end"
            className="z-[1000] w-[180px] rounded-xs !px-none !py-2 flex flex-col gap-2"
          >
            <DropdownMenuItem
              disabled
              className="text-neutral-7 text-body-sm h-8 !rounded-none !opacity-100"
            >
              <Code02Icon className="text-neutral-7 size-4" /> Version v.{appVersion}
            </DropdownMenuItem>
            {profile && (
              <DropdownMenuItem
                onClick={onLogout}
                className="text-neutral-9 text-body-sm h-8 !rounded-none"
              >
                <LogOut01Icon className="text-neutral-9 size-4" /> Log Out
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {isLoggingOut && <Loading fullScreen />}
    </header>
  );
};
